/**
 * License-aware UI (see lib/licenseFeatures.ts).
 *
 *   useLicenseFeaturesLoader() — mount once in App: loads after sign-in, refreshes every
 *                                few minutes, forgets on sign-out
 *   useLicenseFeature('ai')    — 'available' | 'locked' | 'hidden' for one control;
 *                                several features: all are needed (mail AI = 'ai' + 'mail')
 */
import { useEffect } from 'react';
import { useAuthStore } from '../stores';
import {
  classifyFeatures, LICENSE_REFRESH_MS, useLicenseStore,
  type FeatureState, type LicenseFeature,
} from '../lib/licenseFeatures';

export function useLicenseFeaturesLoader(): void {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    const { load, reset } = useLicenseStore.getState();
    if (!isAuthenticated) { reset(); return; }
    void load();
    const timer = setInterval(() => void load(), LICENSE_REFRESH_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated]);
}

export function useLicenseFeature(...features: LicenseFeature[]): FeatureState {
  const access = useLicenseStore((s) => s.access);
  return classifyFeatures(access, features);
}
